import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

import InvoiceType from '~/constants/InvoiceType'
import type { InvoiceOrEstimate } from '~/types'

import {
  type PaymentRequestDialogContextType,
  type PaymentRequestDialogStateType,
  PaymentRequestFlowState,
  type PaymentRequestOnSendStubSuccess,
  type PaymentRequestSendSuccessPayload,
} from '../constants/paymentRequestDialogV2Constants'
import { formatSendToDisplay } from '../utils/paymentRequestDialogV2Utils'

const PaymentRequestDialogContext =
  createContext<PaymentRequestDialogContextType | null>(null)

const initialDialogState: PaymentRequestDialogStateType = {
  clinicNotes: '',
  deliveryMethod: 'email',
  flowState: PaymentRequestFlowState.REQUEST_FORM,
  sendTo: [],
  successPayload: null,
}

export interface PaymentRequestDialogProviderProps {
  children: ReactNode
  invoice: InvoiceOrEstimate
  /**
   * Optional hook after send succeeds (state becomes `SUCCESS` with `successPayload`).
   * TODO(CVC-36749): implement `emailPayment` (or equivalent) inside `sendPayment`; set `SUCCESS` only on API success.
   */
  onSendStubSuccess?: PaymentRequestOnSendStubSuccess
}

export const PaymentRequestDialogProvider = ({
  children,
  invoice,
  onSendStubSuccess,
}: PaymentRequestDialogProviderProps) => {
  const [dialogState, setDialogState] =
    useState<PaymentRequestDialogStateType>(initialDialogState)

  const dialogStateRef = useRef(dialogState)
  dialogStateRef.current = dialogState

  const actions = useMemo(
    () => ({
      resetDialogState: () => {
        setDialogState((prev) => ({
          ...prev,
          clinicNotes: '',
          flowState: PaymentRequestFlowState.REQUEST_FORM,
          successPayload: null,
        }))
      },
      setClinicNotes: (clinicNotes: string) => {
        setDialogState((prev) => ({ ...prev, clinicNotes }))
      },
      setDeliveryMethod: (
        method: PaymentRequestDialogStateType['deliveryMethod'],
      ) => {
        setDialogState((prev) => ({ ...prev, deliveryMethod: method }))
      },
      setSendTo: (recipients: PaymentRequestDialogStateType['sendTo']) => {
        setDialogState((prev) => ({ ...prev, sendTo: recipients }))
      },
      sendPayment: async () => {
        const prev = dialogStateRef.current
        if (prev.sendTo.length === 0) {
          return
        }
        if (
          prev.flowState === PaymentRequestFlowState.SENDING ||
          prev.flowState === PaymentRequestFlowState.SUCCESS
        ) {
          return
        }

        const { deliveryMethod, sendTo } = prev

        setDialogState((p) => ({
          ...p,
          flowState: PaymentRequestFlowState.SENDING,
        }))

        try {
          // TODO(CVC-36749): replace with real API call; catch and map errors when schema is known
          await new Promise<void>((resolve) => {
            setTimeout(() => {
              resolve()
              // reject(new Error('MESSAGE_DIDNT_SEND'))
            }, 2000)
          })

          const payload: PaymentRequestSendSuccessPayload = {
            isDeposit: invoice.type === InvoiceType.ESTIMATE,
            recipientDisplay: formatSendToDisplay(sendTo, deliveryMethod),
          }
          setDialogState((p) => ({
            ...p,
            flowState: PaymentRequestFlowState.SUCCESS,
            successPayload: payload,
          }))
          onSendStubSuccess?.(payload)
        } catch {
          // TODO(CVC-36749): implement error mapping when schema is known
          setDialogState((p) => ({
            ...p,
            flowState: PaymentRequestFlowState.REQUEST_FORM,
          }))
        } finally {
          setDialogState((p) => {
            if (p.flowState !== PaymentRequestFlowState.SENDING) {
              return p
            }
            return {
              ...p,
              flowState: PaymentRequestFlowState.REQUEST_FORM,
            }
          })
        }
      },
    }),
    [invoice, onSendStubSuccess],
  )

  const contextValue = useMemo<PaymentRequestDialogContextType>(
    () => ({
      actions,
      dialogState,
      invoice,
    }),
    [actions, dialogState, invoice],
  )

  return (
    <PaymentRequestDialogContext.Provider value={contextValue}>
      {children}
    </PaymentRequestDialogContext.Provider>
  )
}

export const usePaymentRequestDialog = (): PaymentRequestDialogContextType => {
  const context = useContext(PaymentRequestDialogContext)
  if (!context) {
    throw new Error(
      'usePaymentRequestDialog must be used within a PaymentRequestDialogProvider',
    )
  }
  return context
}
